import { DND_DRAGGABLE_ATTR, DND_DROP_ID_ATTR, DND_DROPPABLE_ATTR, DND_ZONE_ATTR } from './attributes'
import { clearTargetAttributes } from './dropTarget'

// The ghost is the floating copy of the source that follows the pointer.

const GHOST_Z_INDEX = '9999'

function stripTargetingAttributes(node: Element): void {
    node.removeAttribute('id')
    node.removeAttribute(DND_DRAGGABLE_ATTR)
    node.removeAttribute(DND_DROPPABLE_ATTR)
    node.removeAttribute(DND_DROP_ID_ATTR)
    node.removeAttribute(DND_ZONE_ATTR)
    if (node instanceof HTMLElement) clearTargetAttributes(node)
}

// A cloned id would duplicate the source's id in the DOM, and leftover
// targeting attributes could let a stale clone pass as a real item.
function stripGhostIdentity(ghost: HTMLElement): void {
    stripTargetingAttributes(ghost)
    for (const node of ghost.querySelectorAll('*')) stripTargetingAttributes(node)
}

export function createGhost(sourceElement: HTMLElement, rect: { width: number; height: number }): HTMLElement | null {
    const ghost = sourceElement.cloneNode(true)
    if (!(ghost instanceof HTMLElement)) return null
    stripGhostIdentity(ghost)
    ghost.setAttribute('aria-hidden', 'true')
    // Inline styles on purpose: the engine must not depend on any CSS file.
    // pointer-events:none keeps the ghost out of per-frame hit-testing.
    ghost.style.position = 'fixed'
    ghost.style.left = '0'
    ghost.style.top = '0'
    ghost.style.width = `${rect.width}px`
    ghost.style.height = `${rect.height}px`
    ghost.style.margin = '0'
    ghost.style.pointerEvents = 'none'
    ghost.style.zIndex = GHOST_Z_INDEX
    return ghost
}

export function positionGhost(ghost: HTMLElement, x: number, y: number): void {
    // transform instead of left/top: moving the ghost must not trigger layout.
    ghost.style.transform = `translate(${x}px, ${y}px)`
}

// The ghost is cloned before React paints drag classes onto the source, so a
// later class change (from useIsDragging) has to be copied across. Inline
// styles on the ghost — position, transform — are not part of `class` and stay.
export function watchSourceClass(source: HTMLElement, ghost: HTMLElement): () => void {
    const observer = new MutationObserver(() => {
        ghost.className = source.className
    })
    observer.observe(source, { attributes: true, attributeFilter: ['class'] })
    return () => observer.disconnect()
}
